import { useMemo, useCallback, useRef } from "react";
import { UserData, UserStatus } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import { UserCell } from "@/components/controls/DataTable/ScoringTable";
import Text from "@/components/ui/text";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import { useTranslation } from "react-i18next";
import FilterTabs, { FilterTab } from "@/components/ui/FilterTabs";
import SearchInput from "@/components/ui/search-input";
import { getEnumTKey } from "@/utils/strings";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/utils/utils";
import {
  UserTitleCell,
  UserRoleBadge,
  UserActionMenu,
  useUserTableHelpers,
  userStatusColors
} from "./UserTableComponents";

const TRANSLATION_KEY = "settings.userManagement";

interface UserManagementTableProps {
  users: UserData[];
  filterTabs: FilterTab[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  searchTerm: string;
  activeFilter: string;
  selectedUsers: Set<string>;
  selectAll: boolean;
  sorting: any;
  onSearchChange: (value: string) => void;
  onFilterChange: (filter: string) => void;
  onUserSelect: (userId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onDeactivateUser: (userData: UserData) => void;
  onActivateUser: (userData: UserData) => void;
  onEditUser: (userData: UserData) => void;
  isUserManagedByIdentityProvider: (userData: UserData) => boolean;
  onLoadMore: () => void;
  onSortingChange: (sorting: any) => void;
  height?: string;
  className?: string;
}

export const UserManagementTable = ({
  users,
  filterTabs,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  isError,
  searchTerm,
  activeFilter,
  selectedUsers,
  selectAll,
  sorting,
  onSearchChange,
  onFilterChange,
  onUserSelect,
  onSelectAll,
  onDeactivateUser,
  onActivateUser,
  onEditUser,
  isUserManagedByIdentityProvider,
  onLoadMore,
  onSortingChange,
  height = "calc(100vh - 200px)",
  className = ""
}: UserManagementTableProps) => {
  const { t } = useTranslation();
  const { getManagerName, formatDate } = useUserTableHelpers();

  // Search and status tabs narrow the listed users, so a manager is often not
  // among them; managers are fetched by id instead.
  const managerIds = useMemo(
    () =>
      Array.from(
        new Set(
          users
            .map((user) => user.managerId)
            .filter((managerId): managerId is string => !!managerId)
        )
      ),
    [users]
  );
  const { users: managers } = useUsers(managerIds);

  const selectCell = useCallback(
    ({ row }: { row: any }) => {
      const isChecked = selectedUsers?.has(row.original.id) || false;
      return (
        <div
          className={cn(
            "!pl-[2px]",
            !isChecked && "opacity-0 group-hover:opacity-100 transition-opacity"
          )}
        >
          <SingleCheckbox
            id={`select-${row.original.id}`}
            isChecked={selectedUsers.has(row.original.id)}
            onCheckedChange={(checked) =>
              onUserSelect(row.original.id, checked)
            }
            label=""
            className="border-border-secondary-button"
          />
        </div>
      );
    },
    [selectedUsers, onUserSelect]
  );

  const userNameCell = useCallback(
    ({ row }: { row: any }) => <UserCell userData={row.original} showEmail />,
    []
  );

  const onDeactivateUserRef = useRef(onDeactivateUser);
  const onActivateUserRef = useRef(onActivateUser);
  const onEditUserRef = useRef(onEditUser);

  onDeactivateUserRef.current = onDeactivateUser;
  onActivateUserRef.current = onActivateUser;
  onEditUserRef.current = onEditUser;

  const actionsCell = useCallback(
    ({ row }: { row: any }) => (
      <UserActionMenu
        userData={row.original}
        onDeactivate={(userData) => onDeactivateUserRef.current(userData)}
        onActivate={(userData) => onActivateUserRef.current(userData)}
        onEdit={(userData) => onEditUserRef.current(userData)}
        isManagedByIdentityProvider={isUserManagedByIdentityProvider(row.original)}
      />
    ),
    [isUserManagedByIdentityProvider]
  );

  const columns = useMemo<ColumnDef<UserData>[]>(
    () => [
      {
        id: "select",
        header: () => (
          <div className="pr-2">
            <SingleCheckbox
              id="select-all"
              isChecked={selectAll}
              onCheckedChange={onSelectAll}
              label=""
              className="border-border-secondary-button"
            />
          </div>
        ),
        cell: selectCell,
        size: 60,
        minSize: 60,
        maxSize: 60,
        enableSorting: false,
        enableResizing: false,
        meta: {
          textAlign: "center",
          paddingLeft: "pl-4",
          disableDragging: true
        }
      },
      {
        accessorKey: "displayName",
        header: t(`${TRANSLATION_KEY}.table.userName`),
        cell: userNameCell,
        size: 200,
        meta: { textAlign: "start", paddingLeft: "pl-2" }
      },
      {
        accessorKey: "jobTitle",
        header: t(`${TRANSLATION_KEY}.table.title`),
        cell: ({ getValue, row }) => {
          const jobTitle = getValue() as string;
          return <UserTitleCell userData={{ ...row.original, jobTitle }} />;
        },
        size: 150,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "managerId",
        header: t(`${TRANSLATION_KEY}.table.reportsTo`),
        cell: ({ getValue }) => {
          const managerId = getValue() as string;
          const managerName = getManagerName(managerId, managers);
          const isNoData = managerName === t(`${TRANSLATION_KEY}.placeholders.noData`);
          return <Text variant={isNoData ? "bodyTertiary" : "body"}>{managerName}</Text>;
        },
        size: 150,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "roleName",
        header: t(`${TRANSLATION_KEY}.table.role`),
        cell: ({ getValue }) => {
          const role = getValue() as string;
          return <UserRoleBadge role={role} />;
        },
        size: 220,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "areaOfResponsibilityName",
        header: t(`${TRANSLATION_KEY}.table.areaOfResponsibility`),
        cell: ({ getValue }) => {
          const areaOfResponsibilityName = getValue() as string;
          return (
            <Text variant={areaOfResponsibilityName ? "body" : "bodyTertiary"}>
              {areaOfResponsibilityName ||
                t(`${TRANSLATION_KEY}.placeholders.noData`)}
            </Text>
          );
        },
        size: 150,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "createdAt",
        header: t(`${TRANSLATION_KEY}.table.dateAdded`),
        cell: ({ getValue }) => {
          const createdAt = getValue() as string;
          const formattedDate = formatDate(createdAt || "");
          const isNoData = formattedDate === t(`${TRANSLATION_KEY}.placeholders.noData`);
          return <Text variant={isNoData ? "bodyTertiary" : "body"}>{formattedDate}</Text>;
        },
        size: 120,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "lastLoginAt",
        header: t(`${TRANSLATION_KEY}.table.lastLogin`),
        cell: ({ getValue }) => {
          const lastLoginAt = getValue() as string;
          const formattedDate = formatDate(lastLoginAt || "");
          const isNoData = formattedDate === t(`${TRANSLATION_KEY}.placeholders.noData`);
          return <Text variant={isNoData ? "bodyTertiary" : "body"}>{formattedDate}</Text>;
        },
        size: 120,
        meta: { textAlign: "start" }
      },
      {
        accessorKey: "status",
        header: t(`${TRANSLATION_KEY}.table.status`),
        minSize: 200,
        cell: (info) => {
          const valueKey = getEnumTKey(
            "settings.userManagement",
            "statusValues",
            UserStatus,
            info.getValue() as UserStatus
          );
          const statusKey = info.getValue() as UserStatus;
          return (
            <StatusBadge statusColor={userStatusColors[statusKey]}>
              {t(valueKey)}
            </StatusBadge>
          );
        },
        size: 100,
        meta: { textAlign: "start" }
      },
      {
        id: "actions",
        header: "",
        cell: actionsCell,
        size: 60,
        enableSorting: false,
        meta: { textAlign: "center" }
      }
    ],
    [
      selectCell,
      userNameCell,
      actionsCell,
      selectedUsers,
      selectAll,
      onUserSelect,
      onSelectAll,
      getManagerName,
      formatDate,
      t,
      managers
    ]
  );

  const tableOptions = useMemo(
    () => ({
      enableRowSelection: true,
      enableColumnResizing: true
    }),
    []
  );

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
        <FilterTabs
          tabs={filterTabs}
          activeTab={activeFilter}
          onTabChange={onFilterChange}
        />
        <div className="w-80">
          <SearchInput
            value={searchTerm}
            onChange={onSearchChange}
            placeholder={t(`${TRANSLATION_KEY}.search`)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={users}
        height={height}
        tableOptions={tableOptions}
        isDataLoading={isLoading}
        resizableColumns={true}
        headerClassName="border-none bg-surface-elevation0"
        enableColumnReorder
        isInfiniteScroll={true}
        hasMore={hasNextPage && !isError}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={onLoadMore}
        initialSortBy={sorting}
        onSortingChange={onSortingChange}
        manualSorting={true}
        className="flex-1 min-h-0"
      />
    </div>
  );
};

export default UserManagementTable;
